import { Controller } from "@hotwired/stimulus"
import { useWindowResize } from "stimulus-use"
import { Tooltip, Popover, Modal, Dropdown, Collapse, Alert, Carousel, Offcanvas, Tab } from "bootstrap"

// Connects to data-controller="layout"
export default class extends Controller {
  connect() {
    this.initializeBootstrapComponents()

    this.width = window.innerWidth
    this.height = window.innerHeight
    console.log('Window size is: ' + this.width + 'x' + this.height);

    // Bind methods to preserve context
    this.boundTurboLoad = () => {
      this.handleResize()
      this.initializeBootstrapComponents()
    }
    this.boundDOMContentLoaded = () => {
      this.handleResize()
      this.initializeBootstrapComponents()
    }
    this.boundFrameRender = () => {
      this.handleResize()
      this.initializeBootstrapComponents()
    }
    this.boundStreamRender = () => {
      this.initializeBootstrapComponents()
    }
    this.boundChange = () => this.handleResize()
    this.boundTrixFileAccept = (e) => { e.preventDefault() }

    document.addEventListener('turbo:load', this.boundTurboLoad)
    document.addEventListener("DOMContentLoaded", this.boundDOMContentLoaded)
    document.addEventListener("turbo:frame-render", this.boundFrameRender)
    document.addEventListener("turbo:stream-render", this.boundStreamRender)
    document.addEventListener("change", this.boundChange)
    document.addEventListener('trix-file-accept', this.boundTrixFileAccept)

    useWindowResize(this)
  }
  
  disconnect() {
    // Remove event listeners when controller is disconnected
    document.removeEventListener('turbo:load', this.boundTurboLoad)
    document.removeEventListener("DOMContentLoaded", this.boundDOMContentLoaded)
    document.removeEventListener("turbo:frame-render", this.boundFrameRender)
    document.removeEventListener("turbo:stream-render", this.boundStreamRender)
    document.removeEventListener("change", this.boundChange)
    document.removeEventListener('trix-file-accept', this.boundTrixFileAccept)
  }

  initializeBootstrapComponents() {
    this.initializeTooltips()
    this.initializePopovers()
    this.initializeModals()
    this.initializeDropdowns()
    //this.initializeCollapses()
    this.initializeAlerts()
    this.initializeCarousels()
    this.initializeOffcanvas()
    this.initializeTabs()
  }

  initializeTooltips() {
    // Dispose existing tooltips; guard against elements removed mid-transition
    const existingTooltips = document.querySelectorAll('[data-bs-toggle="tooltip"]')
    existingTooltips.forEach(element => {
      const tooltip = Tooltip.getInstance(element)
      if (tooltip) {
        try {
          tooltip.dispose()
        } catch (_e) {
          // Element may have been detached from the DOM while a fade-out
          // transition was in progress (e.g. during a Turbo stream update).
          // Swallowing the error here is intentional — Bootstrap will clean
          // up the orphaned tooltip element on its own.
        }
      }
    })

    // Initialize new tooltips
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
    const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new Tooltip(tooltipTriggerEl))
  }

  initializePopovers() {
    // Dispose existing popovers
    const existingPopovers = document.querySelectorAll('[data-bs-toggle="popover"]')
    existingPopovers.forEach(element => {
      const popover = Popover.getInstance(element)
      if (popover) {
        popover.dispose()
      }
    })

    // Initialize new popovers
    const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]')
    const popoverList = [...popoverTriggerList].map(popoverTriggerEl => new Popover(popoverTriggerEl))
  }

  initializeModals() {
    // Modals are usually handled by specific controllers, but we can initialize them here if needed
    const modalElements = document.querySelectorAll('.modal')
    modalElements.forEach(element => {
      if (!Modal.getInstance(element)) {
        new Modal(element)
      }
    })
  }

  initializeDropdowns() {
    // Initialize dropdowns. Skip the Preact email CSR tree — those toggles are
    // created/destroyed on every signal render; eager Bootstrap instances fight
    // remounts ("doesn't allow more than one instance per element"). Bootstrap's
    // delegated Data API still opens them on click via data-bs-toggle.
    const dropdownElements = document.querySelectorAll('[data-bs-toggle="dropdown"]')
    dropdownElements.forEach(element => {
      if (element.closest('#email-app-root')) return
      if (!Dropdown.getInstance(element)) {
        new Dropdown(element)
      }
    })
  }

  initializeCollapses() {
    // Initialize collapses
    const collapseElements = document.querySelectorAll('[data-bs-toggle="collapse"]')
    collapseElements.forEach(element => {
      if (!Collapse.getInstance(element)) {
        new Collapse(element)
      }
    })
  }

  initializeAlerts() {
    // Initialize alerts
    const alertElements = document.querySelectorAll('.alert[data-bs-dismiss="alert"]')
    alertElements.forEach(element => {
      if (!Alert.getInstance(element)) {
        new Alert(element)
      }
    })
  }

  initializeCarousels() {
    // Initialize carousels
    const carouselElements = document.querySelectorAll('[data-bs-ride="carousel"]')
    carouselElements.forEach(element => {
      if (!Carousel.getInstance(element)) {
        new Carousel(element)
      }
    })
  }

  initializeOffcanvas() {
    // Initialize offcanvas
    const offcanvasElements = document.querySelectorAll('[data-bs-toggle="offcanvas"]')
    offcanvasElements.forEach(element => {
      if (!Offcanvas.getInstance(element)) {
        new Offcanvas(element)
      }
    })
  }

  initializeTabs() {
    // Initialize tabs
    const tabElements = document.querySelectorAll('[data-bs-toggle="tab"]')
    tabElements.forEach(element => {
      if (!Tab.getInstance(element)) {
        new Tab(element)
      }
    })
  }

  windowResize({ width, height, event }) {
    this.width = width
    this.height = height
    this.handleResize()
  }

  handleResize() {
    //console.log('Window resized to: ' + this.width + 'x' + this.height);
    if (document.getElementById("contextmenu")) {
      if (this.width < 1200) {
        document.getElementById("contextmenu").classList.add("offcanvas", "offcanvas-end")
        document.getElementById("contextmenu").firstElementChild.classList.add("offcanvas-body")
        document.getElementById("contextmenu").classList.remove("d-none")
      } else {
        document.getElementById("contextmenu").classList.remove("offcanvas", "offcanvas-end", "d-none")
        document.getElementById("contextmenu").firstElementChild.classList.remove("offcanvas-body")
      }
    }

    if (document.getElementById("breadcrumbs-buttons")) {
      if (this.width < 1200) {
        document.getElementById("breadcrumbs-buttons").classList = "btn-group";
      } else {
        document.getElementById("breadcrumbs-buttons").classList = "";
      }
    }
    if (document.getElementById("tasklist")) {
      if (this.width < 1200) {
        document.getElementById("tasklist").querySelectorAll(".btn-group").forEach(function(btnGroup) {
          btnGroup.classList.replace("btn-group", "btn-group-vertical");
        })
      } else {
        document.getElementById("tasklist").querySelectorAll(".btn-group-vertical").forEach(function(btnGroup) {
          btnGroup.classList.replace("btn-group-vertical", "btn-group");
        })
      }
    }
    
    // Handle button groups in tables (for company index and other table views)
    document.querySelectorAll("table .btn-group, table .btn-group-vertical").forEach(function(btnGroup) {
      if (this.width < 1200) {
        if (btnGroup.classList.contains("btn-group")) {
          btnGroup.classList.replace("btn-group", "btn-group-vertical");
        }
      } else {
        if (btnGroup.classList.contains("btn-group-vertical")) {
          btnGroup.classList.replace("btn-group-vertical", "btn-group");
        }
      }
    }.bind(this))
  }

  logout() {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'push_unsubscribe' }));
    }
  }
}
